/**
 * S25 — Reports · Generated Library (`/reports/library`).
 *
 * Wave 30 — Phase 3b. Migrated off `useTenantData().reports` onto the
 * `/api/reports` REST endpoint via `useEntityList()` + SyncedIndicator.
 * Cross-tenant access is impossible by construction (the JWT-scoped
 * endpoint resolves the tenant from the access token, never the URL).
 */
import React, { useState, useMemo } from "react";
import { FolderArchive, FileSignature, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";

export default function ReportLibrary() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    items: reports,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntityList("/api/reports");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return reports.filter(
      (r) =>
        !term ||
        `${r.id} ${r.name} ${r.framework} ${r.generatedBy}`
          .toLowerCase()
          .includes(term),
    );
  }, [reports, q]);

  if (loading && reports.length === 0) {
    return <Skeleton />;
  }
  if (error) {
    return <ErrorView message={error.message} onRetry={refetch} />;
  }
  if (isCustomer && !reports.length) {
    return <Empty userName={user?.name?.split(" ")[0]} />;
  }

  const signedCount = reports.filter((r) => r.signed).length;
  const totalSize = reports.reduce((a, r) => a + (r.sizeMb || 0), 0);

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="report-library"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Reports · Generated Library</p>
          <h1 className="text-display-md text-ink-primary mt-1">
            Reports Library
          </h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Every ALGOS-generated report — each signed and stamped with the
            audit-chain root for tamper-evident verification.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="report-library-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <KpiTile
          label="Generated reports"
          value={reports.length.toString()}
          delta="archived"
          deltaTone="neutral"
          hint="all-time count"
          icon={FolderArchive}
          tone="tralenex"
          testId="rl-kpi-total"
        />
        <KpiTile
          label="Signed"
          value={signedCount.toString()}
          delta={
            reports.length && signedCount === reports.length
              ? "100% coverage"
              : `${reports.length - signedCount} unsigned`
          }
          deltaTone={signedCount === reports.length ? "up" : "down"}
          hint="tamper-evident"
          icon={FileSignature}
          tone="low"
          testId="rl-kpi-signed"
        />
        <KpiTile
          label="Total size"
          value={`${totalSize.toFixed(1)} MB`}
          delta="across all packs"
          deltaTone="neutral"
          hint="archived storage"
          icon={Download}
          tone="gold"
          testId="rl-kpi-size"
        />
      </div>

      <div
        className="mt-6 surface-card shadow-e1 overflow-hidden"
        data-testid="rl-panel"
      >
        <div className="px-4 py-3 border-b border-border-subtle flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, framework, ID…"
              data-testid="rl-search"
              className="w-full pl-8 pr-3 h-9 rounded-md border border-border-subtle bg-white text-sm placeholder:text-ink-disabled focus:outline-none focus:border-tralenex-500"
            />
          </div>
          <p className="ml-auto text-meta text-ink-secondary tabular-nums">
            {filtered.length} of {reports.length}
          </p>
        </div>
        <table className="w-full text-sm" data-testid="rl-table">
          <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
            <tr>
              {["ID", "Name", "Framework", "Generated", "By", "Size", "Signed", ""].map(
                (h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.id}
                className="border-t border-border-subtle hover:bg-inset/30"
                data-testid={`rl-row-${r.id}`}
              >
                <td className="px-3 py-2 font-mono text-meta text-ink-secondary whitespace-nowrap">
                  {r.id}
                </td>
                <td className="px-3 py-2 text-ink-primary font-medium">{r.name}</td>
                <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">
                  {r.framework}
                </td>
                <td className="px-3 py-2 text-meta font-mono text-ink-secondary tabular-nums whitespace-nowrap">
                  {r.generatedAt}
                </td>
                <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">
                  {r.generatedBy}
                </td>
                <td className="px-3 py-2 text-meta font-mono text-ink-secondary tabular-nums whitespace-nowrap">
                  {(r.sizeMb || 0).toFixed(1)} MB
                </td>
                <td className="px-3 py-2 text-meta uppercase tracking-[0.06em] font-bold whitespace-nowrap text-risk-low">
                  {r.signed ? "Yes" : "—"}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() =>
                      toast.success("Download started", {
                        description: `${r.name} (signed PDF).`,
                      })
                    }
                    data-testid={`rl-dl-${r.id}`}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-border-subtle text-meta text-ink-secondary hover:text-ink-primary"
                  >
                    <Download size={11} /> Download
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-ink-secondary text-sm" data-testid="rl-empty-row">
                  No reports match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="report-library-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[400px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="report-library-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Reports · Generated Library</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load reports</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="report-library-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden"
      data-testid="rl-customer-empty"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Reports · Generated Library</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 text-[15px]">
          No reports yet — generate any pack from the Pre-Built Packs page to fill the library.
        </p>
      </div>
    </div>
  );
}
